import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { jwtDecode } from 'jwt-decode'
import { STORAGE_KEY_ACCESS_TOKEN, STORAGE_KEY_REFRESH_TOKEN } from '../const/constants'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: () => import('../layouts/AppLayout.vue'),
    redirect: { name: 'dashboard' },
    meta: { isAuthRequired: true }, // Все дочерние маршруты требуют авторизации
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'settings-homepage',
        path: 'settings-homepage',
        component: () => import('../pages/settings-homepage/SettingsHomepage.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'projects-categories',
        path: 'projects-categories',
        component: () => import('../pages/projects-categories/CategoriesPage.vue'),
      },
      {
        name: 'reviews',
        path: 'reviews',
        component: () => import('../pages/reviews/ReviewsPage.vue'),
      },
      {
        name: 'skills',
        path: 'skills',
        component: () => import('../pages/skills/skills.vue'),
      },
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        name: 'employee',
        path: 'employee',
        component: () => import('../pages/employee/EmployeePage.vue'),
      },
      {
        name: 'clients',
        path: 'clients',
        component: () => import('../pages/clients/ClientsPage.vue'),
      },
      {
        name: 'articles',
        path: 'articles',
        component: () => import('../pages/articles/ArticlesPage.vue'),
      },
      {
        name: 'article-categories',
        path: 'article-categories',
        component: () => import('../pages/article-categories/CategoriesPage.vue'),
      },
      {
        name: 'services',
        path: 'services',
        component: () => import('../pages/services/services.vue'),
      },
      {
        name: 'feedback',
        path: 'feedback',
        component: () => import('../pages/feedback/FeedbackPage.vue'),
      },
      {
        name: 'job',
        path: 'job',
        component: () => import('../pages/job/JobRequestsPage.vue'),
      },
      {
        path: '/docs',
        meta: { isAuthRequired: true },
        children: [
          {
            name: 'docs-cookies',
            path: 'cookies',
            component: () => import('../pages/docs/cookies.vue'),
          },
          {
            name: 'docs-privacy',
            path: 'privacy',
            component: () => import('../pages/docs/privacy.vue'),
          },
          {
            name: 'docs-personal-data',
            path: 'personal-data',
            component: () => import('../pages/docs/personal-data.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    component: () => import('../layouts/AuthLayout.vue'),
    meta: { isAuthRequired: false },
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
    meta: { isAuthRequired: false },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach((to, from, next) => {
  const accessToken = localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN)
  const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN)

  let isAccessTokenExpired = true
  try {
    const decodedAccessToken = jwtDecode(accessToken || '') as { exp: number }
    isAccessTokenExpired = Date.now() >= (decodedAccessToken?.exp * 1000 || 0)
  } catch {
    isAccessTokenExpired = true
  }

  let isRefreshTokenExpired = true
  try {
    const decodedRefreshToken = jwtDecode(refreshToken || '') as { exp: number }
    isRefreshTokenExpired = Date.now() >= (decodedRefreshToken?.exp * 1000 || 0)
  } catch {
    isRefreshTokenExpired = true
  }

  if (to.meta.isAuthRequired && (!accessToken || (isAccessTokenExpired && isRefreshTokenExpired))) {
    return next({ name: 'login' })
  }

  next()
})

export default router
